<template>
  <el-dialog
    v-model="dialogVisible"
    :title="`设定图预览 - ${settingName}`"
    width="900px"
    class="image-preview-modal"
    destroy-on-close
  >
    <div class="preview-container">
      <!-- 主图显示区 -->
      <div class="main-image-area">
        <div class="image-wrapper">
          <img
            v-if="currentImage"
            :src="currentImage.image_url"
            :alt="settingName"
            @click="showLightbox = true"
          >
          <div v-else class="no-image">
            <el-icon :size="48"><Picture /></el-icon>
            <p>暂无图片</p>
          </div>
        </div>

        <!-- 图片切换按钮 -->
        <el-button
          v-if="images.length > 1"
          class="nav-btn prev-btn"
          circle
          @click="prevImage"
        >
          <el-icon><ArrowLeft /></el-icon>
        </el-button>
        <el-button
          v-if="images.length > 1"
          class="nav-btn next-btn"
          circle
          @click="nextImage"
        >
          <el-icon><ArrowRight /></el-icon>
        </el-button>

        <!-- 图片计数 -->
        <div v-if="images.length > 1" class="image-counter">
          {{ currentIndex + 1 }} / {{ images.length }}
        </div>
      </div>

      <!-- 缩略图列表 -->
      <div v-if="images.length > 1" class="thumbnail-list">
        <div
          v-for="(image, index) in images"
          :key="image.id"
          class="thumb-item"
          :class="{ active: index === currentIndex, 'is-default': image.is_default }"
          @click="selectImage(index)"
        >
          <img :src="image.image_url" :alt="`图片 ${index + 1}`">
          <div v-if="image.is_default" class="thumb-default-badge">默认</div>
        </div>
      </div>

      <!-- 操作按钮区 -->
      <div class="action-area">
        <div class="action-left">
          <el-button
            v-if="currentImage && !currentImage.is_default"
            type="primary"
            @click="handleSetDefault"
          >
            <el-icon><Check /></el-icon>
            选为默认
          </el-button>
          <el-tag v-else-if="currentImage?.is_default" type="success">
            <el-icon><Check /></el-icon>
            当前默认
          </el-tag>
        </div>
        <div class="action-right">
          <el-button @click="showLightbox = true">
            <el-icon><FullScreen /></el-icon>
            放大查看
          </el-button>
          <el-button type="primary" plain @click="handleDownload">
            <el-icon><Download /></el-icon>
            下载图片
          </el-button>
        </div>
      </div>

      <!-- 图片信息 -->
      <div v-if="currentImage" class="image-info">
        <el-descriptions :column="2" size="small" border>
          <el-descriptions-item label="尺寸">
            {{ currentImage.width || '-' }} × {{ currentImage.height || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="文件大小">
            {{ formatFileSize(currentImage.file_size) }}
          </el-descriptions-item>
          <el-descriptions-item label="生成时间" :span="2">
            {{ formatDate(currentImage.created_at) }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </div>

    <!-- 图片放大查看 -->
    <ImageLightbox
      v-model="showLightbox"
      :image-url="currentImage?.image_url"
      :image-name="settingName"
      @download="handleDownload"
    />
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Picture, ArrowLeft, ArrowRight, Check, FullScreen, Download } from '@element-plus/icons-vue'
import ImageLightbox from './ImageLightbox.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  images: {
    type: Array,
    default: () => []
  },
  currentIndex: {
    type: Number,
    default: 0
  },
  settingName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'set-default', 'download'])

// 状态
const dialogVisible = ref(props.modelValue)
const showLightbox = ref(false)
const localIndex = ref(props.currentIndex)

// 监听外部值变化
watch(() => props.modelValue, (val) => {
  dialogVisible.value = val
  if (val) {
    localIndex.value = props.currentIndex
  }
})

watch(() => dialogVisible.value, (val) => {
  emit('update:modelValue', val)
})

watch(() => props.currentIndex, (val) => {
  localIndex.value = val
})

// 计算属性
const currentImage = computed(() => {
  return props.images[localIndex.value] || null
})

// 方法
const selectImage = (index) => {
  localIndex.value = index
}

const prevImage = () => {
  if (localIndex.value > 0) {
    localIndex.value--
  } else {
    localIndex.value = props.images.length - 1
  }
}

const nextImage = () => {
  if (localIndex.value < props.images.length - 1) {
    localIndex.value++
  } else {
    localIndex.value = 0
  }
}

const handleSetDefault = () => {
  if (!currentImage.value) return
  emit('set-default', currentImage.value.id, currentImage.value.setting_id)
}

const handleDownload = () => {
  if (!currentImage.value) return
  emit('download', currentImage.value.image_url)
}

const formatFileSize = (bytes) => {
  if (!bytes) return '-'
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  return `${size.toFixed(2)} ${units[unitIndex]}`
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}
</script>

<style scoped lang="scss">
.image-preview-modal {
  :deep(.el-dialog__body) {
    padding-top: 10px;
  }

  .preview-container {
    .main-image-area {
      position: relative;
      background: var(--el-fill-color-light);
      border-radius: 8px;
      overflow: hidden;
      margin-bottom: 16px;

      .image-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 400px;
        max-height: 500px;

        img {
          max-width: 100%;
          max-height: 500px;
          object-fit: contain;
          cursor: zoom-in;
        }

        .no-image {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: var(--el-text-color-secondary);
          padding: 60px;

          p {
            margin-top: 16px;
          }
        }
      }

      .nav-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;

        &.prev-btn {
          left: 16px;
        }

        &.next-btn {
          right: 16px;
        }
      }

      .image-counter {
        position: absolute;
        bottom: 16px;
        left: 50%;
        transform: translateX(-50%);
        padding: 6px 16px;
        background: rgba(0, 0, 0, 0.6);
        color: #fff;
        border-radius: 20px;
        font-size: 14px;
      }
    }

    .thumbnail-list {
      display: flex;
      gap: 8px;
      margin-bottom: 16px;
      padding: 12px;
      background: var(--el-fill-color-light);
      border-radius: 8px;
      overflow-x: auto;

      .thumb-item {
        position: relative;
        flex-shrink: 0;
        width: 80px;
        height: 80px;
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        border: 2px solid transparent;
        transition: all 0.3s;

        &:hover {
          border-color: #409eff;
        }

        &.active {
          border-color: #409eff;
          box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
        }

        &.is-default {
          &::after {
            content: '默认';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 2px;
            background: #67c23a;
            color: #fff;
            font-size: 11px;
            text-align: center;
          }
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .thumb-default-badge {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 2px;
          background: #67c23a;
          color: #fff;
          font-size: 11px;
          text-align: center;
        }
      }
    }

    .action-area {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      padding: 16px;
      background: var(--el-fill-color-light);
      border-radius: 8px;

      .action-left,
      .action-right {
        display: flex;
        gap: 12px;
      }
    }

    .image-info {
      :deep(.el-descriptions__cell) {
        padding: 8px 12px;
      }
    }
  }
}
</style>
